import { useCallback, useRef, useState } from "react";

// Minimal typing for the Web Speech API (not in lib.dom for webkit-prefixed browsers).
type SpeechResult = { isFinal: boolean; 0: { transcript: string } };
type SpeechEvent = { resultIndex: number; results: ArrayLike<SpeechResult> };
type Recognition = {
  lang: string; interimResults: boolean; continuous: boolean;
  start: () => void; stop: () => void;
  onresult: ((e: SpeechEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
};

function getRecognitionCtor(): (new () => Recognition) | null {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Voice-dictation hook (plan §5.9): start(field, append) streams recognized speech into a target
 * field via the append callback. Only one field listens at a time. Degrades gracefully when the
 * browser has no SpeechRecognition (`supported === false`).
 */
export function useVoiceDictation() {
  const recRef = useRef<Recognition | null>(null);
  const [listeningField, setListeningField] = useState<string | null>(null);
  const supported = !!getRecognitionCtor();

  const stop = useCallback(() => {
    recRef.current?.stop();
    recRef.current = null;
    setListeningField(null);
  }, []);

  const start = useCallback((field: string, append: (text: string) => void) => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;
    recRef.current?.stop();
    const rec = new Ctor();
    rec.lang = "en-US";
    rec.interimResults = false;
    rec.continuous = true;
    rec.onresult = (e) => {
      let text = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) text += r[0].transcript;
      }
      if (text) append(text.trim() + " ");
    };
    rec.onend = () => setListeningField(null);
    rec.onerror = () => setListeningField(null);
    recRef.current = rec;
    setListeningField(field);
    rec.start();
  }, []);

  return { supported, listeningField, start, stop };
}
